import { Node } from 'tree-model'
import { inject, injectable } from 'inversify'
import { NavigationConfig, type NavNode } from './navigation.config'
import { findByRouteId, findRouteNode, getRoutableParent, getBrowsableParent } from './navigation-tree.helpers'
import { Router } from '@core/routing/router'
import type { RouteId } from '@core/routing/router.config'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { action, computed, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { WindowGateway } from '@core/services/window.gateway'

/**
 * Source of truth for the sidebar navigation tree's own UI state: which
 * nodes are expanded, and where in the tree the user is currently browsing.
 * Not the route itself — that is `RouterRepository`'s.
 */
@injectable()
export class NavigationRepository implements UserScopedState {
  /**
   * Which expandable nodes are open, keyed by MenuItem.key (routeId, else the
   * node's translation key — never the rendered label, which changes with the
   * locale).
   *
   * Lives here rather than on NavigationPresenter because it has to outlive
   * the component. SidebarComponent renders null while the sidebar is closed,
   * so NavigationComponent unmounts; useInjection resolves once per component
   * instance and the Presenter is Transient, so reopening used to hand back a
   * fresh Presenter with every node collapsed again. §1.7: state lives in the
   * Repository, and "how long must this survive" is what decides which layer
   * owns it — not whether it is domain data.
   */
  expandedNodesPm = new Map<string, boolean>()

  /**
   * One entry per tree level: the key of the columnView node open at that
   * level. Opening a node at level L replaces anything open at levels >= L.
   * Same lifetime argument as expandedNodesPm.
   */
  columnPathPm: string[] = []

  /**
   * Where the menu is *browsing*, when that differs from where the app *is*.
   * Null means "follow the route", which is the normal state.
   *
   * The menu lists the children of its anchor node, so on a leaf route it has
   * nothing to show — navigating to Books emptied the sidebar of every sibling
   * link. Browsing lets the user walk the anchor up the tree without going
   * anywhere, which is what a sidebar that showed the whole tree would give for
   * free.
   *
   * Same lifetime argument as the two above: the sidebar unmounts when closed,
   * so this cannot live on the Transient Presenter (defect 22).
   */
  browseAnchorPm: RouteId | null = null

  @inject(Router)
  private router!: Router

  @inject(NavigationConfig)
  private navigationConfig!: NavigationConfig

  @inject(Types.IWindowGateway)
  private windowGateway!: WindowGateway

  constructor() {
    makeObservable(this, {
      expandedNodesPm: observable,
      columnPathPm: observable,
      browseAnchorPm: observable,
      currentNode: computed,
      routeNode: computed,
      menuRouteNode: computed,
      browseUpTarget: computed,
      browseUp: action,
      resetBrowseAnchor: action,
      up: action,
      section: action,
      toggleExpanded: action,
      toggleColumn: action,
      closeLastColumn: action,
      resetUserScopedState: action,
    })
  }

  /** The node for the route the app is actually on. */
  get routeNode(): Node<NavNode> | undefined {
    return findByRouteId(this.navigationConfig.tree, this.router.currentRoute.routeId)
  }

  /**
   * Like `routeNode`, but excludes the tree's own wrapper root — used only
   * for "is this menu row the one the app is on" (`NavigationPresenter`'s
   * `current` flag). `routeNode` itself stays ambiguous on purpose: it also
   * feeds breadcrumbs, `currentSelectedVisibleName`, and the Up/Section
   * target label, all of which are deliberately keyed to the *route*
   * ('Home'), not to a specific menu row — see
   * `navigation-tree.helpers.ts`'s `findRouteNode` for why a menu row needs
   * a different answer than a route id does.
   */
  get menuRouteNode(): Node<NavNode> | undefined {
    return findRouteNode(this.navigationConfig.tree, this.router.currentRoute.routeId)
  }

  /**
   * What the menu lists — the browse anchor if the user set one, else the
   * route, unless the route node opts out of drill-down (`anchorsMenu:
   * false`), in which case the menu anchors on its nearest browsable
   * ancestor instead so the node's own row stays listed among its siblings.
   */
  get currentNode(): Node<NavNode> | undefined {
    if (this.browseAnchorPm !== null) return findByRouteId(this.navigationConfig.tree, this.browseAnchorPm)
    const node = this.routeNode
    if (node?.model.anchorsMenu === false) return getBrowsableParent(node) ?? node
    return node
  }

  /**
   * The node browsing would move to, or undefined at the root. A node without a
   * `routesTo` cannot be an anchor, since the anchor is stored as a route id —
   * so the walk skips over any such parent (a group heading, say), and over
   * any parent with nothing visible to show (a detail route's own parent, whose
   * only child is a route-resolution marker) to the next one that qualifies.
   */
  get browseUpTarget(): Node<NavNode> | undefined {
    return this.currentNode && getBrowsableParent(this.currentNode)
  }

  browseUp() {
    const target = this.browseUpTarget
    if (target?.model.routesTo) this.browseAnchorPm = target.model.routesTo
  }

  /**
   * Called on every committed navigation. Browsing is a temporary detour, and
   * leaving it set after the user has gone somewhere would show them a menu
   * for a part of the tree they are no longer in.
   */
  resetBrowseAnchor() {
    this.browseAnchorPm = null
  }

  toggleExpanded(key: string) {
    this.expandedNodesPm.set(key, !this.expandedNodesPm.get(key))
  }

  toggleColumn(level: number, key: string) {
    this.columnPathPm =
      this.columnPathPm[level] === key ? this.columnPathPm.slice(0, level) : [...this.columnPathPm.slice(0, level), key]
  }

  closeLastColumn() {
    this.columnPathPm = this.columnPathPm.slice(0, -1)
  }

  /**
   * Which parts of the menu a user left open is their state, not the app's.
   * It only became user-scoped when it moved off the (Transient, per-mount)
   * Presenter onto this Singleton — the same trap as defect 18, where state
   * gained a longer lifetime than the session that owned it.
   */
  resetUserScopedState(): void {
    this.expandedNodesPm.clear()
    this.columnPathPm = []
    this.browseAnchorPm = null
  }

  navigate(routeId: RouteId, params: Record<string, string> = {}) {
    return this.router.goToId(routeId, params)
  }

  /**
   * "Is this nav target external or internal, and how does each get
   * followed" — one domain decision that `NavigationPresenter` and
   * `CommandPalettePresenter` each used to reimplement around their own
   * `WindowGateway` injection. Owning it here means a third menu-like
   * consumer gets it for free instead of copying the branch a third time.
   *
   * `params` widened for the command palette's entity results (an asset hit
   * carries `{ assetId }`) — every existing caller omits it and is unaffected.
   */
  async follow(target: { routesTo?: RouteId; urlTo?: string; params?: Record<string, string> }): Promise<void> {
    if (target.urlTo) {
      this.windowGateway.open(target.urlTo)
    } else if (target.routesTo) {
      await this.navigate(target.routesTo, target.params ?? {})
    }
  }

  // one level: tree-structural parent.
  //
  // Deliberately off routeNode, not currentNode: this *navigates*, so it has to
  // mean "the parent of the page I am on". Reading the browse anchor instead
  // made Up disappear once the user had browsed to the root, even though the
  // page they were on still had a parent.
  async up() {
    const currentNode = this.routeNode
    const parent = currentNode && getRoutableParent(currentNode)
    if (parent?.model.routesTo) {
      return this.router.goToId(parent.model.routesTo)
    }
  }

  // configurable jump to a group/section root, can skip levels. Off routeNode
  // for the same reason as up().
  async section() {
    const currentNode = this.routeNode
    if (currentNode?.model.sectionTo) {
      return this.router.goToId(currentNode.model.sectionTo)
    }
  }
}
